import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Zap, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }
interface Nuc { x: number; y: number; alive: boolean }
interface Mov { x: number; y: number; vx: number; vy: number; life?: number }

function sphere(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, hi: string, base: string, edge: string) {
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.12, x, y, r)
  g.addColorStop(0, hi); g.addColorStop(0.45, base); g.addColorStop(1, edge)
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

// Phản ứng phân hạch dây chuyền (Lớp 12 — Bài 20): neutron bắn vỡ hạt nhân U-235
// → 2 mảnh + 2-3 neutron mới → tiếp tục bắn vỡ các hạt khác (dây chuyền).
const NuclearReactionSimulation: React.FC<Props> = ({
  title = 'Phản ứng phân hạch dây chuyền',
  description = 'Bắn neutron vào U-235: hạt nhân vỡ thành 2 mảnh và phát thêm neutron, gây phản ứng dây chuyền.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const nuc = useRef<Nuc[]>([])
  const neu = useRef<Mov[]>([])
  const frag = useRef<Mov[]>([])
  const fisRef = useRef(0)
  const [fissions, setFissions] = useState(0)
  const [speed, setSpeed] = useState(1)
  const speedRef = useRef(1)
  useEffect(() => { speedRef.current = speed }, [speed])
  const firstRef = useRef(false)
  const W = 560, H = 320, R = 14

  // vẽ tĩnh: nền + lưới hạt nhân (để thấy hạt nhân ngay từ đầu, trước khi bắn)
  const drawStatic = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, W, H)
    for (const n of nuc.current) { if (n.alive) sphere(ctx, n.x, n.y, R, '#bbf7d0', '#22c55e', '#065f46') }
  }, [])

  const build = useCallback(() => {
    if (raf.current) { cancelAnimationFrame(raf.current); raf.current = null }
    const arr: Nuc[] = []
    for (let r = 0; r < 4; r++) for (let c = 0; c < 6; c++) {
      arr.push({ x: 130 + c * 70, y: 50 + r * 70, alive: true })
    }
    nuc.current = arr; neu.current = []; frag.current = []; fisRef.current = 0; setFissions(0)
    drawStatic()
  }, [drawStatic])
  useEffect(() => { build() }, [build])

  const fire = () => {
    if (!firstRef.current) { firstRef.current = true; onComplete?.() }
    neu.current.push({ x: 6, y: H / 2 + (Math.random() - 0.5) * 60, vx: 3.2, vy: (Math.random() - 0.5) * 0.6 })
    if (!raf.current) raf.current = requestAnimationFrame(tick)
  }

  const tick = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) { raf.current = null; return }
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, W, H)
    // hạt nhân
    for (const n of nuc.current) {
      if (!n.alive) continue
      sphere(ctx, n.x, n.y, R, '#bbf7d0', '#22c55e', '#065f46')
    }
    // neutron + va chạm
    const keep: Mov[] = []
    for (const m of neu.current) {
      m.x += m.vx * speedRef.current; m.y += m.vy * speedRef.current
      let hit = false
      for (const n of nuc.current) {
        if (n.alive && Math.hypot(n.x - m.x, n.y - m.y) < R + 3) {
          n.alive = false; hit = true; fisRef.current++
          // 2 mảnh
          for (let i = 0; i < 2; i++) { const a = Math.random() * Math.PI * 2; frag.current.push({ x: n.x, y: n.y, vx: Math.cos(a) * 1.8, vy: Math.sin(a) * 1.8, life: 60 }) }
          // 2-3 neutron mới
          const k = 2 + Math.round(Math.random())
          for (let i = 0; i < k; i++) { const a = Math.random() * Math.PI * 2; neu.current.push({ x: n.x, y: n.y, vx: Math.cos(a) * 3, vy: Math.sin(a) * 3 }) }
          break
        }
      }
      if (!hit && m.x > -10 && m.x < W + 10 && m.y > -10 && m.y < H + 10) keep.push(m)
      if (!hit) sphere(ctx, m.x, m.y, 4, '#fef9c3', '#fbbf24', '#b45309')
    }
    neu.current = keep
    // mảnh
    frag.current = frag.current.filter((g) => (g.life = (g.life || 0) - 1) > 0)
    for (const g of frag.current) { g.x += g.vx * speedRef.current; g.y += g.vy * speedRef.current; sphere(ctx, g.x, g.y, 6, '#fed7aa', '#f97316', '#9a3412') }

    if (fisRef.current !== fissions) setFissions(fisRef.current)
    if (neu.current.length || frag.current.length) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [fissions])

  useEffect(() => () => { if (raf.current) cancelAnimationFrame(raf.current) }, [])

  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 my-3">
      <h4 className="font-bold text-emerald-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100" />
      <label className="flex items-center gap-2 text-sm mt-3"><span className="w-28 text-gray-600">Tốc độ</span>
        <input type="range" min={0.4} max={2.6} step={0.2} value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="flex-1" />
        <span className="w-12 text-right font-semibold">{speed.toFixed(1)}×</span></label>
      <div className="flex items-center justify-between my-3">
        <span className="text-sm text-gray-600">Số lần phân hạch: <b className="text-emerald-700 text-lg">{fissions}</b></span>
        <div className="flex gap-2">
          <button onClick={fire} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-white font-bold hover:bg-amber-600">
            <Zap className="w-4 h-4" /> Bắn neutron</button>
          <button onClick={build} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">
            <RotateCcw className="w-4 h-4" /> Đặt lại</button>
        </div>
      </div>
    </div>
  )
}
export default NuclearReactionSimulation
